import {
  TextInput,
  Textarea,
  Stack,
  NumberInput,
  Switch,
  Text,
  Image,
  Loader,
  Center,
  ActionIcon,
  Box,
  Group,
  Button,
} from "@mantine/core";
import { Dropzone, type FileRejection } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconUpload, IconPhoto, IconX, IconTrash } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useState, useEffect } from "react";
import {
  OrganMapTemplate,
  organMapTemplatesService,
} from "../../../services/admin/organMapTemplates.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

const ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

export interface OrganMapTemplateFormValues {
  name: string;
  organName: string;
  description: string;
  imageUrl: string;
  svgContent: string;
  width: number | "";
  height: number | "";
  isActive: boolean;
}

interface OrganMapTemplateFormProps {
  formId?: string;
  initialValues?: OrganMapTemplate;
  onSubmit: (values: OrganMapTemplateFormValues) => void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
}

export default function OrganMapTemplateForm({
  formId = "organ-map-template-form",
  initialValues,
  onSubmit,
}: OrganMapTemplateFormProps) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const form = useForm<OrganMapTemplateFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      organName: initialValues?.organName || "",
      description: initialValues?.description || "",
      imageUrl: initialValues?.imageUrl || "",
      svgContent: initialValues?.svgContent || "",
      width: initialValues?.width ?? "",
      height: initialValues?.height ?? "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      name: required("Template name is required"),
      organName: required("Organ name is required"),
    },
  });

  // Resolve existing S3 key to download URL for preview
  useEffect(() => {
    const imageUrl = initialValues?.imageUrl;
    setImageLoaded(false);
    if (imageUrl && !imageUrl.startsWith("http")) {
      setLoadingPreview(true);
      organMapTemplatesService
        .getDownloadUrl(imageUrl)
        .then((res) => {
          setPreviewUrl(res.url);
        })
        .catch(() => {
          setPreviewUrl(null);
        })
        .finally(() => {
          setLoadingPreview(false);
        });
    } else if (imageUrl) {
      setPreviewUrl(imageUrl);
    }
  }, [initialValues?.imageUrl]);

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploading(true);
    try {
      const { url, key } = await organMapTemplatesService.getPresignedUrl(
        file.name,
        file.type
      );
      await organMapTemplatesService.uploadToS3(url, file);
      form.setFieldValue("imageUrl", key);
      setImageLoaded(false);
      setPreviewUrl(URL.createObjectURL(file));
    } catch (error: any) {
      notifications.show({
        title: "Upload failed",
        message: error?.message || "Failed to upload image. Please try again.",
        color: "red",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleReject = (fileRejections: FileRejection[]) => {
    const rejection = fileRejections[0];
    if (!rejection) return;

    const errorCode = rejection.errors[0]?.code;
    if (errorCode === "file-too-large") {
      notifications.show({
        title: "File too large",
        message: "Image must be less than 5 MB.",
        color: "red",
      });
    } else if (errorCode === "file-invalid-type") {
      notifications.show({
        title: "Invalid file type",
        message: "Only PNG, JPG, SVG, and WEBP images are accepted.",
        color: "red",
      });
    } else {
      notifications.show({
        title: "File rejected",
        message: "Please select a valid image file.",
        color: "red",
      });
    }
  };

  const handleRemoveImage = () => {
    form.setFieldValue("imageUrl", "");
    setPreviewUrl(null);
  };

  const hasImage = !!form.values.imageUrl;

  return (
    <form
      id={formId}
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="e.g. Prostate 12-Core Biopsy Map"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <TextInput
          label="Organ Name"
          placeholder="e.g. Prostate, Breast, Colon"
          withAsterisk
          {...form.getInputProps("organName")}
        />

        <Textarea
          label="Description"
          placeholder="Brief description of this organ map template"
          minRows={2}
          {...form.getInputProps("description")}
        />

        {/* Image Upload */}
        <Box>
          <Text size="sm" fw={500} mb={4}>
            Template Image
          </Text>

          {loadingPreview ||
          (hasImage && !previewUrl && initialValues?.imageUrl) ? (
            <Center
              w={300}
              h={200}
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
              }}
            >
              <Loader size="sm" />
            </Center>
          ) : hasImage && previewUrl ? (
            <Box
              pos="relative"
              w={300}
              h={200}
              style={{
                display: "inline-block",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
              }}
            >
              {!imageLoaded && (
                <Center pos="absolute" w={300} h={200} style={{ zIndex: 1 }}>
                  <Loader size="sm" />
                </Center>
              )}
              <Image
                src={previewUrl}
                alt="Template preview"
                w={300}
                h={200}
                fit="contain"
                radius="sm"
                onLoad={() => setImageLoaded(true)}
                fallbackSrc="data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23868e96' font-size='14'%3EImage unavailable%3C/text%3E%3C/svg%3E"
              />
              <ActionIcon
                variant="filled"
                color="red"
                size="sm"
                pos="absolute"
                top={4}
                right={4}
                onClick={handleRemoveImage}
                aria-label="Remove image"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Box>
          ) : hasImage && !previewUrl ? (
            <Center
              w={300}
              h={200}
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: 4,
                flexDirection: "column",
              }}
            >
              <Text size="sm" c="dimmed" mb={4}>
                Image uploaded but preview unavailable
              </Text>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                onClick={handleRemoveImage}
              >
                Remove image
              </Button>
            </Center>
          ) : (
            <Dropzone
              onDrop={handleDrop}
              onReject={handleReject}
              maxSize={MAX_FILE_SIZE}
              accept={ACCEPTED_MIME_TYPES}
              multiple={false}
              loading={uploading}
            >
              <Group
                justify="center"
                gap="xl"
                mih={120}
                style={{ pointerEvents: "none" }}
              >
                <Dropzone.Accept>
                  <IconUpload size={40} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={40} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconPhoto size={40} stroke={1.5} />
                </Dropzone.Idle>

                <div>
                  <Text size="sm" inline>
                    Drag an image here or click to select
                  </Text>
                  <Text size="xs" c="dimmed" inline mt={7}>
                    PNG, JPG, SVG, or WEBP — max 5 MB
                  </Text>
                </div>
              </Group>
            </Dropzone>
          )}
        </Box>

        <Textarea
          label="SVG Content"
          placeholder="Paste SVG markup here"
          minRows={4}
          autosize
          maxRows={12}
          styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
          {...form.getInputProps("svgContent")}
        />

        <Group grow>
          <NumberInput
            label="Width"
            placeholder="e.g. 800"
            min={1}
            {...form.getInputProps("width")}
          />
          <NumberInput
            label="Height"
            placeholder="e.g. 600"
            min={1}
            {...form.getInputProps("height")}
          />
        </Group>

        <Switch
          label="Active"
          mb="md"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
